// UploadResearchData.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { setDay } from "date-fns";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createDataCollectionEntry, createLegalResearchData, getInternalLegalResearchRequest, getLegalResearchRequest, updateLegalResearchData, updateLegalResearchRequest } from "services/legalResearchService";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";

interface uploadResearchDataProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    researchData?: any;
}

const UploadResearchData: React.FC<uploadResearchDataProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onClose,
    researchData,
}) => {
    const { t } = useTranslation();
    const [dataSummary, setDataSummary] = useState(researchData?.data_summary || "");
    const [dataName, setDataName] = useState(researchData?.data_name || "");
    const [dataFile, setDataFile] = useState<any>(researchData?.datafile || null);
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [dataCollector, setDataCollector] = useState("");
    const [dataCollectorTeam, setDataCollectorTeam] = useState<{ id: string; full_name: string }[]>(
        (researchData?.data_collection ?? []).map((member: any) => ({
            ...member,
            id: member.id,
            full_name: member.full_name,
        }))

    );

    const modalContentRef = useRef<HTMLDivElement>(null);

    // Keep the form in sync when opening to add (cleared) or edit (prefilled),
    // including switching between different selected data entries.
    useEffect(() => {
        if (!isOpen) return;
        setDataSummary(researchData?.data_summary || "");
        setDataName(researchData?.data_name || "");
        setDataFile(researchData?.datafile || null);
        setDataCollectorTeam(
            (researchData?.data_collection ?? []).map((member: any) => ({
                ...member,
                id: member.id,
                full_name: member.full_name,
            }))
        );
        setValidationError(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, researchData?.id]);

    const handleDataCollectors = (value: string) => {
        setDataCollector(value);
    };
    const getInitials = (str: string) =>
        str.split(" ").map(s => s[0]).join("").toUpperCase();

    const handleAddCollector = () => {
        if (dataCollector.trim() !== "") {
            setDataCollectorTeam((prev) => [
                ...prev,
                { id: Date.now().toString(), full_name: dataCollector.trim() }
            ]);
            setDataCollector("");
        }
    };

    const handleRemoveCollector = (id: string) => {
        setDataCollectorTeam((prev) => prev.filter((member) => member.id !== id));
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isEdit = !!researchData?.id;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);

        // A file is required only when creating a brand-new entry.
        if (!isEdit && !dataFile) {
            setValidationError(t("PleaseAttachDataFile"));
            return;
        }

        setSubmitting(true);

        try {
            const data = new FormData();
            if (request.research_draft_file) {
                data.append(
                    "status",
                    LEGAL_RESEARCH_REQUEST_STATUS.draft_submitted.value
                );
            }
            if (request.requested_by === "internal")
                data.append("internal_research_request", request.id.toString());
            else
                data.append("research_request", request.id.toString());
            data.append("data_summary", dataSummary);
            data.append("data_name", dataName);
            // Only send a file when a new one was actually selected (a Blob/File,
            // not the existing URL string from an edited record).
            if (dataFile && typeof dataFile !== "string") {
                data.append("file", dataFile);
                data.append("datafile", dataFile);
            }
            data.append("uploaded_by", contextData.user.user_id);

            let dataResponse: any;
            if (isEdit) {
                dataResponse = await updateLegalResearchData(
                    contextData.token,
                    researchData.id,
                    data
                );
            } else {
                dataResponse = await createLegalResearchData(contextData.token, data);
                for (const member of dataCollectorTeam) {
                    const prosecutorData = new FormData();
                    if (request.requested_by === "internal")
                        prosecutorData.append("internal_research_request", request.id.toString());
                    else
                        prosecutorData.append("research_request", request.id.toString());
                    prosecutorData.append("research_data_file", dataResponse.id.toString());
                    prosecutorData.append("full_name", member.full_name);
                    await createDataCollectionEntry(contextData.token, prosecutorData);
                }
            }
            let response: any;
            if (request.requested_by === "internal")
                response = await getInternalLegalResearchRequest(contextData.token, request.id);
            else
                response = await getLegalResearchRequest(request.id, contextData.token);
            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            if (isEdit) {
                showToast(t("LegalResearchDataUpdated") || "Research data updated.", "success");
            } else {
                showToast(t("LegalResearchProsecutorsAssigned"), "success");
                showToast(t("LegalResearchDataUploaded"), "success");
            }
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadLegalResearchData"), "error");
        } finally {
            setSubmitting(false);
        }

        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setDataFile(file);
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {isEdit
                        ? t("EditResearchData") || "Edit Research Data"
                        : t("UploadResearchData")}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="dataName"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("DataName")}
                            </label>
                            <input
                                className="bg-gray-50 py-3 mb-4 w-full px-2 outline-none"
                                placeholder={t("Name")}
                                type="text"
                                value={dataName}
                                onChange={(e) => setDataName(e.target.value)}
                            />
                            <label
                                htmlFor="dataSummary"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("DataSummary")}
                            </label>
                            <textarea
                                id="data_summary"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere")}
                                value={dataSummary}
                                onChange={(e) => setDataSummary(e.target.value)}
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("DataFile")}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                        <div className="mb-6">
                            <label
                                htmlFor="select-prosecutor"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("InsertDataCollectors")}
                            </label>
                            <div className="relative mb-3">
                                <input
                                    className="bg-gray-50 py-3 w-full px-2 outline-none"
                                    placeholder={t("Name")}
                                    type="text"
                                    value={dataCollector}
                                    onChange={(e) => handleDataCollectors(e.target.value)}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleAddCollector}
                                className="mb-4 text-[#AA232F] hover:text-blue-800 text-sm"
                            >
                                {t("AddDataCollectorToTeam")}
                            </button>

                            <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar scrollbar-hide">
                                {dataCollectorTeam.length === 0 && (
                                    <p className="text-gray-500 text-sm italic">
                                        {t("NoCollectorsAddedYet")}
                                    </p>
                                )}
                                {dataCollectorTeam.map((member) => (
                                    <div
                                        key={member.id}
                                        className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 shadow-sm"
                                    >
                                        <div className="w-10 h-10 rounded-full mr-3 bg-gray-700 text-white flex items-center justify-center font-bold text-sm select-none">
                                            {getInitials(member.full_name)}
                                        </div>

                                        <div className="flex-grow">
                                            <p className="font-semibold text-gray-800">
                                                {member.full_name}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveCollector(member.id)}
                                                className="text-gray-400 hover:text-red-500"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="#AA232F"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md flex items-center justify-center"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {t("Submitting")}
                                </>
                            ) : (
                                t("Submit")
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadResearchData;